import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  GetAllSimTagsByTypeParams,
  GetFilteredSimsParams,
} from 'lib/api/requests/sim/sim.requests.types';
import { ACTIVITY_LOGS_PAGE_SIZE } from 'views/SIMDetail/config';
import iotApi from '../../api/iotApi.client';
import { SimQueryKey } from './sim.hooks.keys';

export function useSimCardDetail(params: { iccid: string }) {
  const { iccid } = params;

  return useQuery({
    queryKey: [SimQueryKey.SIM_CARD_DETAIL, iccid],
    queryFn: async () => {
      const res = await iotApi.sim.getSimCardDetail({ iccid });

      return res.data;
    },
    enabled: !!iccid,
  });
}

export function useActivePortInRequestData(params: { iccid: string }) {
  const { iccid } = params;

  return useQuery({
    queryKey: [SimQueryKey.ACTIVE_PORTIN_REQUEST_DATA, iccid],
    queryFn: async () => {
      const res = await iotApi.sim.getActivePortInRequestData({ iccid });

      return res.data;
    },
    enabled: !!iccid,
  });
}

export function useFilteredSims(params: GetFilteredSimsParams) {
  return useQuery({
    queryKey: [SimQueryKey.FILTERED_SIMS, JSON.stringify(params)],
    queryFn: async () => {
      const res = await iotApi.sim.getFilteredSims(params);

      return res;
    },
    staleTime: Infinity,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useAllSimTags(params: GetAllSimTagsByTypeParams) {
  return useQuery({
    queryKey: [SimQueryKey.GET_ALL_SIM_TAGS, params.simType],
    queryFn: async () => {
      const res = await iotApi.sim.getAllSimTags(params);

      return res;
    },
    enabled: !!params.simType,
  });
}

export function useWifiCallingData(params: { iccid: string }) {
  const { iccid } = params;

  return useQuery({
    queryKey: [SimQueryKey.WIFI_CALLING_DATA, iccid],
    queryFn: async () => {
      const res = await iotApi.sim.getWifiCallingDetails({ iccid });

      return res.wifiCallingResp ?? null;
    },
    enabled: !!iccid,
  });
}

export function useSimActivityLogs(params: { iccid: string; limit: number }) {
  const { iccid } = params;

  return useInfiniteQuery({
    queryKey: [SimQueryKey.SIM_ACTIVITY_LOGS, iccid],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await iotApi.sim.getActivityLogs({
        ...params,
        offset: pageParam, // pageParam value is generated by the `getNextPageParam` function below
      });

      return res;
    },
    enabled: !!params.iccid,
    getNextPageParam: (lastResponse, allResponses) => {
      const logsCount = allResponses
        .map((r) => r.data.length)
        .reduce((a, b) => a + b, 0);

      // Return null to indicate there are no more pages if:
      // 1. We've already loaded all available logs, or
      // 2. The last response had fewer items than a full page size
      if (
        logsCount >= lastResponse.count ||
        lastResponse.count < ACTIVITY_LOGS_PAGE_SIZE
      )
        return null;

      // otherwise, we return the next offset i.e. page number * page size
      return allResponses.length * ACTIVITY_LOGS_PAGE_SIZE;
    },
  });
}

export function useESimsQuantity() {
  return useQuery({
    queryKey: [SimQueryKey.ESIMS_QUANTITY],
    queryFn: async () => {
      const res = await iotApi.sim.getESimsQuantity();

      return res;
    },
    refetchOnWindowFocus: true,
  });
}
